import { Link, Navigate, Outlet, createRootRoute, useRouterState } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { useEffect, useState } from "react";

import type { UserRead } from "@/api-client/generated";
import { useUserMe } from "@/api-client/queries";
import { BrandMark } from "@/components/ui/Brand";
import { Badge } from "@/components/ui/Badge";
import { NavMenu } from "@/components/ui/NavMenu";
import { AvatarMenu } from "@/components/ui/AvatarMenu";
import { InstallBanner } from "@/components/ui/InstallBanner";
import { MobileNav } from "@/components/ui/MobileNav";
import { DOC_TABS, MASTER_ITEMS, TOOLS_ITEMS, reportItemsFor } from "@/components/ui/nav-items";
import { useAuthMe } from "@/hooks/useAuthMe";

export const Route = createRootRoute({
  component: RootLayout,
});

function headerDisplayName(p: UserRead | undefined, emailFallback?: string): string {
  if (!p) return emailFallback ?? "";
  const n = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return n || p.email || emailFallback || "";
}

function AvatarRichMenu({
  profile,
  authEmail,
  isAdmin,
}: {
  profile: UserRead | undefined;
  authEmail: string | undefined;
  isAdmin: boolean;
}) {
  const [imgOk, setImgOk] = useState(true);
  const email = profile?.email ?? authEmail ?? "";
  const name = headerDisplayName(profile, authEmail);
  const url = profile?.avatar_url?.trim();
  useEffect(() => setImgOk(true), [url]);
  const initial = (name[0] || email[0] || "?").toUpperCase();
  // For now AvatarMenu uses the initial. When avatar_url is set, the
  // single image swap can be added later — keeps clutter low here.
  void url;
  void imgOk;
  void setImgOk;
  return <AvatarMenu email={email} initial={initial} isAdmin={isAdmin} />;
}

function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const skipAuth = pathname === "/login";

  const authQuery = useAuthMe({ enabled: !skipAuth });
  const userMeQuery = useUserMe({ enabled: !skipAuth });

  if (!skipAuth && authQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-sm text-neutral-600">
        <span className="inline-flex items-center gap-3">
          <span className="size-2 rounded-full bg-accent-500 animate-pulse" />
          Checking session…
        </span>
      </div>
    );
  }

  if (!skipAuth && authQuery.isError) {
    return <Navigate to="/login" />;
  }

  // /login renders without the chrome.
  if (skipAuth) {
    return <Outlet />;
  }

  const user = authQuery.data;
  const isAdmin = user?.groups?.includes("admin") ?? false;

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-border">
        <div className="mx-auto max-w-7xl flex items-center justify-between gap-3 sm:gap-6 px-4 sm:px-6 py-3">
          <div className="flex items-center gap-4 sm:gap-8 min-w-0">
            <Link to="/" className="shrink-0">
              <BrandMark />
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {DOC_TABS.map((tab) => (
                <Link
                  key={tab.to}
                  to={tab.to}
                  className="px-3 py-1.5 rounded-md text-sm text-neutral-600 hover:text-navy-900 hover:bg-neutral-100"
                  activeProps={{ className: "bg-navy-50 text-navy-900 font-medium" }}
                  activeOptions={tab.to === "/bilty" ? { exact: true } : undefined}
                >
                  {tab.label}
                </Link>
              ))}
              <NavMenu label="Masters" items={MASTER_ITEMS} />
              <NavMenu label="Reports" items={reportItemsFor(isAdmin)} />
              <NavMenu label="Tools" items={TOOLS_ITEMS} />
            </nav>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              aria-label="Notifications"
              className="hidden sm:inline-flex size-9 items-center justify-center rounded-md border border-border bg-white text-neutral-600 hover:text-navy-800"
            >
              <Bell className="size-4" />
            </button>
            <AvatarRichMenu
              profile={userMeQuery.data}
              authEmail={user?.email}
              isAdmin={isAdmin}
            />
            <MobileNav
              isAdmin={isAdmin}
              userLabel={headerDisplayName(userMeQuery.data, user?.email) || undefined}
            />
          </div>
        </div>

        <div className="border-t border-border/70 bg-neutral-50/60">
          <div className="mx-auto max-w-7xl flex items-center gap-3 px-4 sm:px-6 py-2 text-xs">
            <span className="text-neutral-500">Financial Year</span>
            <Badge tone="navy">2026-27</Badge>
            {user && (
              <>
                <span className="text-neutral-400">·</span>
                <span className="text-neutral-500">Role</span>
                <Badge tone={isAdmin ? "accent" : "neutral"}>
                  {isAdmin ? "admin" : "user"}
                </Badge>
              </>
            )}
            <span className="ml-auto text-neutral-400 hidden sm:inline">
              erp.ailogistics.co.in
            </span>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 py-6 sm:py-8 canvas-dots">
        <Outlet />
      </main>

      <footer className="border-t border-border bg-white">
        <div className="mx-auto max-w-7xl flex items-center justify-between gap-4 px-4 sm:px-6 py-3 text-xs text-neutral-500">
          <span>AI Logistics · Faridabad, Haryana · GSTIN 06DRAPK9794R1ZP</span>
          <span>{new Date().getFullYear()}</span>
        </div>
      </footer>
      <InstallBanner />
    </div>
  );
}
